import { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { forAlumni } from '../../../story/StoryService';
import './AlumniStories.css';

function when(value: string | null | undefined) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Stars({ rating }: { rating: number | null }) {
    const score = Number(rating);
    const hasRating = Number.isInteger(score) && score >= 1 && score <= 5;
    return (
        <span className={hasRating ? 'alumni-story-rating' : 'alumni-story-rating empty'} aria-label={hasRating ? `Rating ${score} of 5` : 'No rating'}>
            {[1, 2, 3, 4, 5].map((value) => (
                <span key={value} className={hasRating && score >= value ? 'on' : ''}>★</span>
            ))}
        </span>
    );
}

const AlumniStories = () => {
    const { id } = useParams();
    const { state } = useContext(SmartContext);
    const model = state?.internal?.model || {};
    const name = [model.firstName, model.lastName].filter(Boolean).join(' ');
    const [stories, setStories] = useState<any[] | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        if (!id) return undefined;
        forAlumni(id).then((response) => {
            if (!cancelled) setStories(Array.isArray(response.data) ? response.data : []);
        }).catch((err) => {
            if (!cancelled) setError(err?.response?.data?.message || 'Stories could not be loaded.');
        });
        return () => {
            cancelled = true;
        };
    }, [id]);

    return (
        <div className="educationDetails-detail tab-white-block alumni-stories">
            <div className="blockHeader">
                <div className="row g-2 align-items-center">
                    <div className="col">
                        <div className="title">
                            Stories of <span>{name || 'this alumnus'}</span>
                        </div>
                    </div>
                    {!!stories?.length && (
                        <div className="col-auto">
                            <span className="alumni-story-count">{stories.length === 1 ? '1 story' : `${stories.length} stories`}</span>
                        </div>
                    )}
                </div>
            </div>

            {error && <div className="alert alert-warning mb-0">{error}</div>}
            {!stories && !error && <div className="text-gray-64">Loading stories…</div>}
            {stories && !stories.length && !error && <div className="alumni-story-empty">No stories for this alumnus.</div>}

            {(stories || []).map((story) => (
                <div className={`alumni-story-row p-4 bg-light rounded border border-secondary border-opacity-10`} key={story.id}>
                    <div className="row g-3 align-items-start">
                        <div className="col">
                            <div className="alumni-story-top mt-2">
                                <div className="alumni-story-top-main">
                                    {(story.roles || []).map((role: string) => (
                                        <span key={role} className={`alumni-story-role ${role === 'Narrator' ? 'narrator' : 'protagonist'}`}>{role}</span>
                                    ))}
                                    <Stars rating={story.rating} />
                                </div>
                                {when(story.createdAt) && <span className="alumni-story-date">{when(story.createdAt)}</span>}
                            </div>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="label font-14 font-500 text-gray-64">Narrator</div>
                                    <div className="label-detail font-18 font-500">{story.narratorName || '—'}</div>
                                </div>
                                <div className="col-md-4">
                                    <div className="label font-14 font-500 text-gray-64">Protagonist</div>
                                    <div className="label-detail font-18 font-500">{story.protagonistName || '—'}</div>
                                </div>
                                <div className="col-md-4">
                                    <div className="label font-14 font-500 text-gray-64">Institute</div>
                                    <div className="label-detail font-18 font-500">{story.instituteName || '—'}</div>
                                </div>
                                <div className="col-md-6">
                                    <div className="label font-14 font-500 text-gray-64">Why noteworthy</div>
                                    <div className="label-detail font-16 font-500">{story.whyNoteworthy || '—'}</div>
                                </div>
                                <div className="col-md-6">
                                    <div className="label font-14 font-500 text-gray-64">Categories</div>
                                    {(story.categories || []).length ? (
                                        <div className="alumni-story-tags">
                                            {(story.categories || []).map((item: any) => (
                                                <span key={item.code || item.label}>{item.label || item.code}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="label-detail font-16 font-500">—</div>
                                    )}
                                </div>
                                <div className="col-md-12">
                                    <div className="label font-14 font-500 text-gray-64">Markers</div>
                                    {(story.markers || []).length ? (
                                        <div className="alumni-story-tags markers">
                                            {(story.markers || []).map((item: any) => (
                                                <span key={item.markerField || item.label}>{item.label || item.markerField}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="label-detail font-16 font-500">—</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-auto">
                            <Link to={`/support/story/${story.id}`} className="btn-add-section">
                                <span>View story</span>
                            </Link>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AlumniStories;
